import { z } from 'zod';
import type { ExerciseTypeConfig } from '../registry/types';
import type { 
  MultipleChoiceExerciseDefinition, 
  MultipleChoiceExerciseSubmission,
  MultipleChoiceEvaluationDetail 
} from '../types';
import { multipleChoiceSubmissionSchema } from '../types/schemas';

/**
 * Get the normalized max score from a definition
 */
function getNormalizedMaxScore(definition: MultipleChoiceExerciseDefinition, fallback: number): number {
  const maxScore = definition.maxScore;
  if (typeof maxScore === 'number' && Number.isFinite(maxScore) && maxScore > 0) {
    return maxScore;
  }
  return fallback;
}

/**
 * MultipleChoice exercise type configuration
 */
export const multipleChoiceConfig: ExerciseTypeConfig<
  'MultipleChoice',
  MultipleChoiceExerciseDefinition,
  MultipleChoiceExerciseSubmission,
  MultipleChoiceEvaluationDetail
> = {
  type: 'MultipleChoice',
  isAutoGradable: true,
  submissionSchema: multipleChoiceSubmissionSchema,

  defaultMaxScore: (definition) => {
    const correctCount = definition.options.filter((o) => o.isCorrect).length;
    return getNormalizedMaxScore(definition, Math.max(1, correctCount));
  },

  evaluate: (definition, submission) => {
    const correctOptionIndices = definition.options
      .map((option, index) => (option.isCorrect ? index : -1))
      .filter((index) => index >= 0);

    const correctOptionIds = definition.options
      .map((option) => (option.isCorrect && option.optionId ? option.optionId : null))
      .filter((optionId): optionId is string => Boolean(optionId));

    // Build selection set
    const selectedSet = new Set<number>();
    const orderedSelections: number[] = [];

    const addSelection = (index: number | null | undefined) => {
      if (typeof index === 'number' && Number.isInteger(index) && index >= 0 && !selectedSet.has(index)) {
        selectedSet.add(index);
        orderedSelections.push(index);
      }
    };

    // Process optionIds first, then indices
    (submission.selectedOptionIds ?? []).forEach((optionId) => {
      const matchIndex = definition.options.findIndex((option) => option.optionId === optionId);
      if (matchIndex >= 0) {
        addSelection(matchIndex);
      }
    });

    (submission.selectedOptionIndices ?? []).forEach((index) => {
      addSelection(index);
    });

    const selectedIndices = orderedSelections;
    const correctIndexSet = new Set(correctOptionIndices);
    const truePositives = selectedIndices.filter((index) => correctIndexSet.has(index)).length;
    const falsePositives = selectedIndices.filter((index) => !correctIndexSet.has(index)).length;

    const maxScore = getNormalizedMaxScore(definition, Math.max(1, correctOptionIndices.length));
    const allOptionsCorrect =
      definition.options.length > 0 && correctOptionIndices.length === definition.options.length;
    const denominator = correctOptionIndices.length + falsePositives;
    const scoreFraction = denominator > 0 ? truePositives / denominator : 0;
    const score = allOptionsCorrect ? maxScore : Math.round(scoreFraction * maxScore);
    const isFullyCorrect =
      allOptionsCorrect ||
      (correctOptionIndices.length > 0 &&
        truePositives === correctOptionIndices.length &&
        falsePositives === 0);

    const optionEvaluations = definition.options.map((option, index) => {
      const isCorrectAnswer = !!option.isCorrect;
      const isSelected = selectedSet.has(index);
      return {
        index,
        optionId: option.optionId ?? undefined,
        label: option.label,
        isCorrectAnswer,
        isSelected,
        isCorrectSelection: isSelected && isCorrectAnswer,
      };
    });

    return {
      score,
      maxScore,
      isFullyCorrect,
      details: {
        kind: 'MultipleChoice',
        selectedOptionIndices: selectedIndices,
        selectedOptionIds: submission.selectedOptionIds ?? [],
        correctOptionIndices,
        correctOptionIds,
        optionEvaluations,
      },
    };
  },

  hydrateDetails: (serverDetails: unknown) => {
    const details = serverDetails as Record<string, unknown> | null ?? {};
    const rawOptions = Array.isArray(details.options) ? details.options : [];

    const options = rawOptions.map((option: any, index: number) => ({
      optionId: option?.optionId ?? option?.multipleChoiceOptionId ?? option?.id,
      label: option?.label ?? '',
      isCorrect: Boolean(option?.isCorrect),
      order: option?.order ?? index,
    }));

    return {
      question: details.question ?? '',
      options,
    };
  },

  createDefault: (order) => ({
    type: 'MultipleChoice' as const,
    question: '',
    options: [
      { optionId: undefined, label: '', isCorrect: true, order: 0 },
      { optionId: undefined, label: '', isCorrect: false, order: 1 },
    ],
  }),
};
